import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Button, clipboard } from '@gitbook/spine-react';

import { CodeBlock, type CodeBlockProps } from './CodeBlock';
import { CodeBlockLine } from './CodeBlockLine';
import {
    highlightPlainCode,
    resolveCodeBlockSyntax,
    useCodeHighlightingStyles,
    useLoadSyntaxes,
} from './syntax-highlighting';

export interface PlainCodeBlockProps extends Omit<CodeBlockProps, 'children'> {
    code: string;
    syntax: string;
    showCopyButton?: boolean;
}

/**
 * Non-editable code block.
 */
export function PlainCodeBlock(props: PlainCodeBlockProps): React.ReactElement {
    const { code, syntax, showCopyButton = true, toolbar, ...rest } = props;

    const syntaxInfo = resolveCodeBlockSyntax(syntax);
    const resolvedSyntax = syntaxInfo.prismComponent ?? 'text';
    useLoadSyntaxes([resolvedSyntax]);

    const highlightingStyles = useCodeHighlightingStyles();
    const lines = highlightPlainCode(code, syntaxInfo.prismComponent ?? 'text', highlightingStyles);
    return (
        <CodeBlock
            {...rest}
            toolbar={
                <>
                    {toolbar ? <div className="mr-2">{toolbar}</div> : null}
                    {showCopyButton ? (
                        <Button
                            label="Copy"
                            leadingIcon={Icon.Copy}
                            kind="secondary"
                            size="small"
                            iconOnly
                            onPress={() => clipboard.writeText(code)}
                        />
                    ) : null}
                </>
            }
        >
            {lines.map((line, index) => (
                <CodeBlockLine key={index} number={index + 1}>
                    {line}
                </CodeBlockLine>
            ))}
        </CodeBlock>
    );
}
